/**
 * BulkAddCourseSection helpers — pure utility functions consumed by
 * the Bulk Add Course Section feature.
 *
 * This file is intentionally free of React or API dependencies so it
 * can be unit-tested in isolation.  It provides:
 *   - `mapSectionListToRows`      — legacy row-flattening (pre-CDG)
 *   - `formatDateRange`           — date formatting for display
 *   - `createSectionFilterPayload` — the core payload builder that
 *     translates the UI's three-way selection model into the filter
 *     array the backend expects.
 *   - `buildBulkAddSectionPayload` — merges filters with survey dates
 *     from the parent Add Course Section modal.
 */
import { formatDateWithTime } from './formatDateWithTime'

/** Legacy flat row shape used before the CDG migration. */
interface Row {
  id: string
  Course_id: string
  title: string
  end_date: string
}

interface Filter {
  field: string
  op?: string
  value: string | string[]
}

/** Survey date values captured from the parent Add Course Section modal. */
export interface BulkAddSectionDateValues {
  startTimestamp?: Date | string | null
  endTimestamp?: Date | string | null
  /** Maps to API field `earlyAccessReleaseTimestamp_2`. */
  earlyAccessReleaseTimestamp1?: Date | string | null
  /** Maps to API field `earlyAccessReleaseTimestamp_1`. */
  earlyAccessReleaseTimestamp2?: Date | string | null
}

export interface BulkAddSectionPayload {
  filters: Filter[]
  startTimestamp?: string
  endTimestamp?: string
  earlyAccessReleaseTimestamp_1?: string
  earlyAccessReleaseTimestamp_2?: string
  /** Update-existing mode: only sent by the Bulk Update flow. */
  isPresent?: boolean
  /** Tri-state opt-out: omit = don't change; true = out; false = in. */
  isOptedOut?: boolean
}

/**
 * Extra options for the Bulk Update flow. `isPresent` marks the request as
 * update-existing; `isOptedOut` is the resolved tri-state (undefined = leave
 * unchanged). Kept separate from date values so the create/bulk-add path is
 * untouched.
 */
export interface BulkUpdateSectionOptions {
  isPresent?: boolean
  isOptedOut?: boolean
}

/**
 * Legacy helper — flattens raw section objects into the shape the old
 * (non-CDG) DataGrid expected.  Kept for backwards compatibility with
 * any code paths that still use the flat format.
 */
export function mapSectionListToRows (data: any[]): Row[] {
  return data.map(item => ({
    id: item.sectionId,
    Course_id: item.abbreviation,
    title: item.title,
    end_date: formatDateRange(item.startTimestamp, item.endTimestamp)
  }))
}

/** Formats two ISO timestamps into a compact "MM/DD/YY - MM/DD/YY" range. */
export function formatDateRange (start: string, end: string): string {
  if (!start || !end) return ''
  const startDate = new Date(start)
  const endDate = new Date(end)
  const format = (date: Date): string => {
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const yy = String(date.getFullYear()).slice(-2)
    return `${mm}/${dd}/${yy}`
  }
  return `${format(startDate)} - ${format(endDate)}`
}

/**
 * Builds the final filter payload for the bulk-add API.
 *
 * The selection model supports three mutually exclusive modes:
 *
 *   **Case 1 — Manual selection** (`selectAll === false`, `selectedRowsData` has entries):
 *     The user hand-picked specific rows.  Appends an `in` filter so
 *     the backend processes only those sectionIds.
 *
 *   **Case 2 — Select All with exclusions** (`selectAll === true`, `unselectedIds` has entries):
 *     The user clicked "Select All" then unchecked a few rows.
 *     Appends a `not_in` filter so the backend processes all matching
 *     sections *except* the excluded ones.
 *
 *   **Case 3 — Select All, no exclusions** (`selectAll === true`, `unselectedIds` is empty):
 *     No explicit section_id filter is appended.  The backend interprets
 *     the absence of a section_id filter as "apply to every section
 *     that matches the user-defined filters".
 *
 * In all cases the user-defined filters (date, text, number conditions)
 * are always included as the base of the payload.
 */
export function createSectionFilterPayload (
  selectAll: boolean,
  unselectedIds: string[],
  selectedRowsData: string[] | undefined,
  transformedFilterData: Filter[] | undefined
): Filter[] {
  const filters = [...(transformedFilterData || [])]
  if (selectAll) {
    if (unselectedIds.length === 0) {
      // Case 3: no section_id filter — backend selects all matches.
    } else {
      // Case 2: exclude specific sections from the "all" set.
      filters.push({
        field: 'section_id',
        op: 'not_in',
        value: unselectedIds
      })
    }
  } else if (selectedRowsData && selectedRowsData.length > 0) {
    // Case 1: include only the explicitly selected sections.
    filters.push({
      field: 'section_id',
      op: 'in',
      value: selectedRowsData
    })
  }
  return filters
}

/**
 * Builds the full bulk-add API payload by merging section filters with
 * survey dates from the parent Add Course Section modal.
 *
 * Date field mapping matches the single-section create flow:
 *   - `earlyAccessReleaseTimestamp1` (form) → `earlyAccessReleaseTimestamp_2` (API)
 *   - `earlyAccessReleaseTimestamp2` (form) → `earlyAccessReleaseTimestamp_1` (API)
 *
 * Rolling-release projects omit release-date fields entirely.
 */
export function buildBulkAddSectionPayload (
  selectAll: boolean,
  unselectedIds: string[],
  selectedRowsData: string[] | undefined,
  transformedFilterData: Filter[] | undefined,
  sectionDates: BulkAddSectionDateValues | undefined,
  isRollingRelease: boolean,
  updateOptions?: BulkUpdateSectionOptions
): BulkAddSectionPayload {
  const filters = createSectionFilterPayload(
    selectAll,
    unselectedIds,
    selectedRowsData,
    transformedFilterData
  )

  const payload: BulkAddSectionPayload = { filters }

  if (sectionDates?.startTimestamp) {
    payload.startTimestamp = formatDateWithTime(sectionDates.startTimestamp)
  }

  if (sectionDates?.endTimestamp) {
    payload.endTimestamp = formatDateWithTime(sectionDates.endTimestamp)
  }

  if (!isRollingRelease) {
    if (sectionDates?.earlyAccessReleaseTimestamp1) {
      payload.earlyAccessReleaseTimestamp_2 = formatDateWithTime(sectionDates.earlyAccessReleaseTimestamp1)
    }

    if (sectionDates?.earlyAccessReleaseTimestamp2) {
      payload.earlyAccessReleaseTimestamp_1 = formatDateWithTime(sectionDates.earlyAccessReleaseTimestamp2)
    }
  }

  // Bulk Update (update-existing) mode. `isPresent: true` tells the backend to
  // update already-attached sections; `isOptedOut` is only sent when the user
  // actively chose Opted out/Opted in (undefined = leave unchanged).
  if (updateOptions?.isPresent === true) {
    payload.isPresent = true
    if (updateOptions.isOptedOut !== undefined) {
      payload.isOptedOut = updateOptions.isOptedOut
    }
  }

  return payload
}
